import React from "react";

import { css, cx } from "@emotion/css";
import { useTranslation } from "react-i18next";

import { useInclusionCenterContent } from "@/hooks/useInclusionCenterContent.ts";
import { useStyles } from "@/hooks/useStyles.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { Typography } from "@/uiKit/atoms/Typography.tsx";
import { StyleClasses } from "@/uiKit/atoms/utils/AtomTypes.ts";
import { GroupedInclusionAcceptedSuggestion } from "@/uiKit/molecules/InclusionCenter/GroupedInclusion/GroupedInclusionAcceptedSuggestion.tsx";
import { InclusionAgentAcceptedSuggestion } from "@/uiKit/molecules/InclusionCenter/InclusionAgent/InclusionAgentAcceptedSuggestion.tsx";
import { InclusionAgentSuggestion } from "@/uiKit/molecules/InclusionCenter/InclusionAgent/InclusionAgentSuggestion.tsx";
import { NurseInclusionSuggestion } from "@/uiKit/molecules/InclusionCenter/NurseInclusion/NurseInclusionSuggestion.tsx";

export type InclusionCenterCardContentProps = {
  onClose?: () => void;
  className?: string;
  classes?: StyleClasses<"root" | "empty">;
};

export const InclusionCenterCardContent: React.FC<InclusionCenterCardContentProps> = ({
  onClose,
  className,
  classes,
}) => {
  const styles = useStyles(makeStyles);
  const { t } = useTranslation();
  const inclusionCenterItems = useInclusionCenterContent();

  if (inclusionCenterItems.length === 0) {
    return (
      <div className={cx(styles.noMoreSuggestions, classes?.empty)}>
        <Typography>{t("inclusionAgent.noMoreSuggestions")}</Typography>
      </div>
    );
  }
  return inclusionCenterItems.map(inclusion => {
    switch (inclusion.type) {
      case "group":
        switch (inclusion.patient.status) {
          case "visible_by_doctor":
            return (
              <GroupedInclusionAcceptedSuggestion
                className={cx(className, classes?.root, "grouped-inclusion-suggestion")}
                key={inclusion.patient.id}
                suggestion={inclusion.patient}
              />
            );
        }
      case "agent":
        switch (inclusion.patient.status) {
          case "patient_accepted":
            return (
              <InclusionAgentAcceptedSuggestion
                className={cx(className, classes?.root, "inclusion-agent-accepted-suggestion")}
                key={inclusion.patient.id}
                suggestion={inclusion.patient}
              />
            );
          case "suggested_to_doc":
            return (
              <InclusionAgentSuggestion
                className={cx(className, classes?.root, "inclusion-agent-suggestion")}
                key={inclusion.patient.id}
                onAction={() => {
                  if (inclusionCenterItems.length <= 1) {
                    onClose && onClose();
                  }
                }}
                suggestion={inclusion.patient}
              />
            );
        }
      case "nurse":
        switch (inclusion.patient.status) {
          case "suggested_to_doc":
            return (
              <NurseInclusionSuggestion
                className={cx(className, classes?.root, "nurse-inclusion-suggestion")}
                key={inclusion.patient.id}
                onAction={() => {
                  if (inclusionCenterItems.length <= 1) {
                    onClose && onClose();
                  }
                }}
                suggestion={inclusion.patient}
              />
            );
          default:
            return null;
        }
    }
  });
};

const makeStyles = (theme: Theme) => ({
  container: css({}),
  noMoreSuggestions: css({
    padding: theme.spacing(16),
    paddingTop: 0,
    fontStyle: "italic",
    textAlign: "center",
    verticalAlign: "middle",
    alignSelf: "center",
    justifySelf: "center",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  }),
});
